import { useEffect, useRef, useState } from 'react';
import { revenueApi, formatCurrency } from '../utils/api';
import { isoDaysBetween, formatIsoRange } from '../utils/revenueFormat';
import { currentMonthRange, PRESETS_MONTHLY } from '../utils/period';
import { useIsMobile } from '../utils/useIsMobile';
import ConfirmModal from './ConfirmModal';
import PeriodPicker from './PeriodPicker';

// ONE form, ONE amount. This file used to export two — a per-category batch
// grid (a field for every קטגוריה) and a single-entry form with a category
// <select>. Both are gone: a POS Z-report is one number for the restaurant, and
// asking the user to split it across מזון / משקאות / חד״פ was asking for a
// figure that does not exist.
//
// The range is chosen with the SAME PeriodPicker the reports use, so "this
// month" is one tap and "from date to date" is the same control the user
// already knows, instead of the two bare <input type="date"> boxes that were
// here before.
//
// The 409 discipline is unchanged: the server returns the overlapping entries,
// one replace dialog lists them, confirming re-sends with overwrite: true. The
// dialog's list is informative ONLY — the server recomputes overlaps inside its
// transaction, so a stale list cannot corrupt anything.

const replaceDialog = (conflicts) => ({
  title: 'הכנסות חופפות בטווח',
  body: [
    ...conflicts.map(c =>
      `${formatIsoRange(c.date_from, c.date_to)} · ${formatCurrency(c.amount)}`,
    ),
    'אישור יחליף את הימים החופפים — הרשומות הקיימות יקוצצו או יימחקו בהתאם.',
  ],
  confirmLabel: 'החלפת הימים החופפים',
  cancelLabel: 'ביטול',
  danger: true,
});

/**
 * The live helper under the amount: how long the range is, what that averages
 * per day, and what the figure is worth ex-VAT.
 *
 * The ex-VAT line is here because the field asks for the GROSS Z-report number
 * while every percentage on the analysis screens is computed net. Showing the
 * conversion at the point of entry is what stops "but I typed 320,000" later.
 */
function EntryHelper({ from, to, amount, vatRate }) {
  const days = isoDaysBetween(from, to);
  const n = Number(amount);
  if (!days || !Number.isFinite(n) || String(amount).trim() === '') return null;
  const net = Math.round((n / (1 + (vatRate || 0) / 100)) * 100) / 100;
  return (
    <span style={{ fontSize: 12, color: 'var(--ink-muted)' }}>
      {days === 1 ? 'יום אחד' : `${days} ימים`}
      {days > 1 && ` · ${formatCurrency(Math.round((n / days) * 100) / 100)} ליום בממוצע`}
      {` · ללא מע״מ ${formatCurrency(net)}`}
    </span>
  );
}

/**
 * Create (no `entry`) or edit (an API row) one revenue entry.
 *
 * `initialFrom`/`initialTo` prefill the range — the coverage strip passes a
 * tapped day, the analysis banner passes the first missing range.
 */
export default function RevenueEntryForm({
  entry = null,
  initialFrom = '',
  initialTo = '',
  vatRate = 18,
  onSaved,
  onCancel,
  cancelLabel = 'ביטול',
  stickyActions = false,
  // Page chrome, not action-row behaviour: `.docs-filter-wrap`/`.period-bar`
  // are only correct when this form sits directly in the app's own scrolling
  // page, under the fixed 60px navbar they dock beneath. True by default
  // because that's every caller except RevenueQuickAddModal's short,
  // independently-scrolling dialog, which opts out explicitly — a modal has
  // no navbar to dock under, and the offset instead forced the picker down
  // over the label beneath it (sticky still reserves only its unclamped
  // static height in flow). Kept independent of `stickyActions`: a future
  // modal wanting a sticky save row must not also inherit page-chrome
  // positioning it has nothing to dock against.
  stickyFilterBar = true,
  onDirtyChange,
}) {
  const isMobile = useIsMobile();
  const defaults = currentMonthRange();
  const [from, setFrom] = useState(entry?.date_from || initialFrom || defaults.from);
  const [to, setTo] = useState(entry?.date_to || initialTo || defaults.to);
  const [amount, setAmount] = useState(entry != null ? String(entry.amount) : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [conflicts, setConflicts] = useState(null);

  // Range prefills arrive through props while the component stays MOUNTED —
  // never remount it with a `key` to re-seed them. That wiped whatever the user
  // had already typed on every calendar tap, and it is a pinned regression.
  const seeded = useRef(`${initialFrom}|${initialTo}`);
  useEffect(() => {
    const next = `${initialFrom}|${initialTo}`;
    if (entry || !initialFrom || !initialTo || seeded.current === next) return;
    seeded.current = next;
    setFrom(initialFrom);
    setTo(initialTo);
  }, [entry, initialFrom, initialTo]);

  const dirty = String(amount).trim() !== '';
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);

  const send = async (overwrite) => {
    setSaving(true); setError('');
    const body = { date_from: from, date_to: to, amount: Number(amount), overwrite };
    try {
      if (entry) await revenueApi.update(entry.id, body);
      else await revenueApi.save(body);
      setConflicts(null);
      if (!entry) setAmount('');
      onSaved?.();
    } catch (err) {
      if (err?.response?.status === 409) {
        setConflicts(err.response.data?.conflicts || []);
      } else {
        setError(err?.response?.data?.error || 'שגיאה בשמירת ההכנסה');
      }
    } finally {
      setSaving(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (!from || !to) { setError('יש לבחור טווח תאריכים'); return; }
    if (from > to) { setError('תאריך התחלה חייב להיות לפני תאריך הסיום'); return; }
    const n = Number(amount);
    if (String(amount).trim() === '' || !Number.isFinite(n) || n < 0) {
      setError('סכום הפדיון חייב להיות אפס או חיובי'); return;
    }
    send(false);
  };

  return (
    <form dir="rtl" onSubmit={submit}>
      {conflicts && (
        <ConfirmModal
          {...replaceDialog(conflicts)}
          onConfirm={() => send(true)}
          onCancel={() => setConflicts(null)}
        />
      )}
      {error && (
        <div className="badge-red" style={{ display: 'block', marginBottom: 10, padding: '8px 12px', borderRadius: 8 }}>
          {error}
        </div>
      )}

      <div
        className={stickyFilterBar ? (isMobile ? 'period-bar' : 'docs-filter-wrap') : undefined}
        style={{ marginBottom: 12 }}
      >
        <PeriodPicker
          dateFrom={from}
          dateTo={to}
          onChange={(f, t) => { setError(''); setFrom(f); setTo(t); }}
          presets={PRESETS_MONTHLY}
          variant={isMobile ? 'bar' : 'pill'}
        />
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label style={{ fontSize: 12.5, color: '#475569', flex: '1 1 200px' }}>
          פדיון לתקופה (כולל מע״מ)
          {/* The edit form can render on the same screen as the create form
              (RevenueEntrySheet shows both while an entry is open), so the two
              amount fields must not share a label — a screen reader would
              announce two identical controls with no way to tell them apart. */}
          <input
            className="form-control" type="number" min="0" step="0.01" inputMode="decimal"
            aria-label={entry ? 'סכום הפדיון בהזנה שנערכת' : 'סכום הפדיון'} value={amount}
            onChange={e => { setError(''); setAmount(e.target.value); }}
            placeholder="0.00"
            style={{ padding: '6px 10px', width: '100%' }}
          />
        </label>
      </div>
      <div style={{ marginTop: 4 }}>
        <EntryHelper from={from} to={to} amount={amount} vatRate={vatRate} />
      </div>
      {/* ₪0 is a real answer, not an absence — "we were open and sold nothing".
          A day the restaurant was CLOSED is a fact about the day, marked on the
          coverage strip, and must not be typed as a zero. */}
      {String(amount).trim() !== '' && Number(amount) === 0 && (
        <p style={{ fontSize: 12, color: 'var(--ink-muted)', marginTop: 6 }}>
          0 = היינו פתוחים ולא נמכר כלום. אם הייתם סגורים — סמנו את הימים כסגורים בלוח הכיסוי.
        </p>
      )}

      <div
        className={stickyActions && dirty ? 'category-lines-actions-stuck' : 'modal-actions'}
        style={{ marginTop: 12 }}
      >
        {/* Cancel first, confirm last — the confirming button belongs at the
            trailing edge like every other dialog footer. */}
        {onCancel && (
          <button className="btn btn-secondary" type="button" onClick={onCancel}>{cancelLabel}</button>
        )}
        <button className="btn btn-primary" type="submit" disabled={saving}>
          {saving ? 'שומר…' : entry ? 'עדכון ההזנה' : 'שמירת הפדיון'}
        </button>
      </div>
    </form>
  );
}
